﻿import { App } from './config/app.js'

export function parseBool(value)
{
    return value === true || String(value).toLowerCase() === 'true' || parseInt(value) > 0
}

export function elementToggle(element, show)
{
	if (show == undefined)
		show = element.classList.contains('hidden')

	if (show)
	{
		element.classList.remove('hidden')
	}
	else
	{
		element.classList.add('hidden')
	}

	element.querySelectorAll('[aria-required]').forEach(el =>
	{
		el.required = show
		el.setAttribute('aria-required', show);
	})
}

export function formTrackValidity(element)
{
	element
		.querySelectorAll('input,select,textarea')
		.forEach(el =>
		{
			if(el.type !== 'file')
				el.addEventListener('change', () => el.value = el.value.trim())
			el.addEventListener('invalid', () => el.classList.add(App.cssClass.invalid))
			el.addEventListener('input', () => clearValidity(el))
		})
}

export function clearValidity(element)
{
	element.setCustomValidity('')
	if (element.validity.valid) element.classList.remove(App.cssClass.invalid)
}

export function currentUser(attr = false)
{
	let model = App.Store.model('customer', App.Config.customerId)
	return attr ? model?.attributes[attr] : model
}

export function i18n(key, data = undefined)
{
	key = key.toLowerCase()
	const ENV = currentStore('environment')

	// #region Log all i18n usage when in debug
	if (ENV == 'DEBUG')
	{
		if (!App.Usage.I18n[key])
			App.Usage.I18n[key] = 0
		++App.Usage.I18n[key]
	}
	// #endregion

	let item = App.Store.model('i18n', key)
	if (!item)
		console.warn('Missing localization:', key)

	if (!item || !item?.attributes?.text)
		return ENV == 'DEBUG' ? key : '?'

	if (typeof data === 'object' && data?.hasOwnProperty('lookupProperty')) // Hbs options parameter
		return item.attributes.text

	let args = Array.from(arguments)
	args.shift()
	return processStringAsTemplate(item.attributes.text, args)
}

export function i18nIfExists(key, data = undefined)
{
	if (App.Store.model('i18n', key))
		return i18n(key, data)
	else
		return undefined
}

export function currentStore(attr = false)
{
	let model = App.Store.model('store', App.Config.storeId)

	if (attr)
	{
		if (model?.attributes?.hasOwnProperty(attr))
			return model?.attributes[attr]
		return null
	}

	return model
}

export function hasRole(role)
{
	let customer = currentUser();
	if (!App.Config.customerId || !customer || !customer?.attributes || !customer?.attributes?.roles)
		return role == 'Guest' ? true : false
	return customer?.attributes?.roles ? customer.attributes.roles.indexOf(App.Roles[role]) != -1 : false
}

export function hasAdministrativeRole()
{
	return hasRole('Administrator')
		|| hasRole('ManageProduct')
		|| hasRole('ManageCustomer')
		|| hasRole('ManageOrder')
		|| hasRole('ManageStore')
		|| hasRole('Marketing')
}

export function setCookie(name, value, days)
{
	var date = new Date(),
		expires = "";

	if (days)
	{
		date.setTime(date.getTime() + (days * 86400000));
		expires = "; expires=" + date.toGMTString();
	}

	var secure = location.protocol.indexOf('https') > -1 ? '; Secure' : '';

	document.cookie = name + "=" + value + expires + secure + "; path=/";
}

export function getCookie(name)
{
	var nameEQ = name + "=",
		ca = document.cookie.split(";"),
		cookie;

	for (var i in ca)
	{
		/*jshint -W116 */
		var cookiePart = ca[i];
		while (cookiePart.charAt(0) == " ")
		{
			cookiePart = cookiePart.substring(1, cookiePart.length);
		}

		if (cookiePart.indexOf(nameEQ) === 0)
		{
			cookie = cookiePart.substring(nameEQ.length, cookiePart.length);
			break
		}
	}

	return cookie;
}

// Return a list of errors for a password change request
export function getPasswordErrors(password, confirmPassword, oldPassword)
{
	let errors = []

	if (oldPassword && oldPassword === password)
		errors.push('equalsoldpassword')
	if (password.length < 8)
		errors.push('lengthvalidation')
	if (!password.match('(?=.*[a-z])(?=.*[A-Z])'))
		errors.push('upperlowercase')
	if (!password.match('\\d'))
		errors.push('numbers')
	if (!password.match('\\W'))
		errors.push('specialcharacters')
	if (password !== confirmPassword)
		errors.push('enteredpasswordsdonotmatch')

	return errors
}

class ErrorAPI extends Error
{
	constructor(error)
	{
		super(parseErrorMessage(error))
		this.title = error.title
		this.status = parseInt(error.status)
		this.meta = error.meta
		this.code = getErrorCode(error)?.toLowerCase()
	}
}

export function getError(e)
{
	if (!e)
		return null
	if (e instanceof Error)
		return new ErrorAPI(e)
	let errors = e.detail ? (e.detail.model?.errors || e.detail.errors) : e.errors
	return errors ? new ErrorAPI(errors[0]) : (e.detail ? new ErrorAPI(e.detail.error) : null)
}

export function getErrorCode(err)
{
	if (err instanceof Event)
		err = getError(err)

	if (!err)
		return null

	let errCode = 'unknown'

	if (err && err.code)
		errCode = err.code + ''
	else if (err instanceof Error && err.message)
		errCode = err.message.replaceAll(' ', '_')

	return errCode.toLowerCase()
}

export function getErrorMessage(e)
{
	let message = null
	const error = getError(e)
	if (error instanceof ErrorAPI)
		message = error.message
	else
		message = parseErrorMessage(error)

	return message
}

export function parseErrorMessage(error)
{
	let errorMessage = error?.title || error?.message || null

	if (!errorMessage)
	{
		const errorCode = getErrorCode(error)
		errorMessage = i18nIfExists('apex.ui.error.' + errorCode) || i18nIfExists('apex.ui.error.unknown') || 'Unknown Error'
	}

	const template = Handlebars.compile(errorMessage)
	let message = template({ err: error })
	if (errorMessage.indexOf('err.meta.errors') == -1)
	{
		if (error?.meta?.errors?.length > 0)
		{
			message += '<ul>'
			for (let _error of error.meta.errors)
			{
				message += '<li>' + _error + '</li>'
			}
			message += '<ul>'
		}
	}
	return message
}

export function getNetworkFromCreditCardNumber(creditCardNumber)
{
	const regAmex = new RegExp("^3[47][0-9]{13}$")
	const regDinersClub = new RegExp("^3(?:0[0-5]|[68][0-9])[0-9]{11}$")
	const regDiscover = new RegExp("^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$")
	const regJCB = new RegExp("^(?:2131|1800|35\d{3})\d{11}$")
	const regMastercard = new RegExp("^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$")
	const regUnionPay = new RegExp("^(62[0-9]{14,17})$")
	const regVisa = new RegExp("^4[0-9]{12}(?:[0-9]{3})?$")

	if (regAmex.test(creditCardNumber))
		return 'AMEX'
	else if (regDinersClub.test(creditCardNumber))
		return 'DinersClub'
	else if (regDiscover.test(creditCardNumber))
		return 'Discover'
	else if (regJCB.test(creditCardNumber))
		return 'JCB'
	else if (regMastercard.test(creditCardNumber))
		return 'Mastercard'
	else if (regUnionPay.test(creditCardNumber))
		return 'UnionPay'
	else if (regVisa.test(creditCardNumber))
		return 'VISA'
	else
		return 'Unknown'
}

export function appendElement(target, tag, attributes, onLoad)
{
	const element = document.createElement(tag)
	if (onLoad) element.onload = onLoad
	for (let attr in attributes)
	{
		element.setAttribute(attr, attributes[attr])
	}
	return target.appendChild(element)
}

export function appendScript(url, onLoad)
{
	let element = document.querySelector('script[src="' + url + '"]')
	if (element)
	{
		if (onLoad) onLoad.call(element, null)
		return element
	}

	return appendElement(
		document.body,
		'script',
		{
			'type': 'application/javascript',
			'src': url,
		},
		onLoad
	)
}

export function appendStyle(url, onLoad)
{
	let element = document.querySelector('link[href="' + url + '"]')
	if (element)
	{
		if (onLoad) onLoad.call(element, null)
		return element
	}

	return appendElement(
		document.body,
		'link',
		{
			'rel': 'stylesheet',
			'type': 'text/css',
			'href': url,
		},
		onLoad
	)
}

export function processStringAsTemplate(str, data = undefined)
{
	if (data === undefined || data === null || data === '')
		return str

	let parsed = str

	if (Array.isArray(data))
	{
		data.forEach((value, index) =>
		{
			if (typeof value === 'string' || typeof value === 'number')
				parsed = parsed.replace(`{${index}}`, value.toString())
		})

		return parsed
	}
	else if (typeof data === 'object')
	{
		let tpl = Handlebars.compile(parsed)
		return tpl(data)
	}

	Object.values(arguments).forEach((value, index) =>
	{
		if (index > 0 && (typeof value === 'string' || typeof value === 'number'))
			parsed = parsed.replace(`{${index - 1}}`, value.toString())
	})

	return parsed
}

// Turn an object into a single-level object (primarily for data -> query)
export function flattenObject(data)
{
	let query = {}

	Object.entries(data).forEach(entry =>
	{
		if (Array.isArray(entry[1]))
		{
			query[entry[0]] = entry[1].join(',')
		}
		else if (typeof entry[1] == 'object')
		{
			Object.entries(entry[1]).forEach(_entry => query[`${entry[0]}[${_entry[0]}]`] = _entry[1])
		}
		else
		{
			query[entry[0]] = entry[1]
		}
	})

	if (query['filter[to]'])
		query['filter[to]'] = moment(query['filter[to]']).endOf('day').format()

	return query
}

export function modelLoading(model, isLoading)
{
	if (arguments.length == 1)
	{
		return model._loading
	}

	model._loading = !!isLoading
}

export function loadScript(url)
{
	return new Promise((resolve, reject) =>
	{
		const script = document.createElement('script')
		script.src = url
		script.async = true
		script.onerror = reject
		script.onload = script.onreadystatechange = function()
		{
			const loadState = this.readyState

			if (loadState && loadState !== 'loaded' && loadState !== 'complete') return

			script.onload = script.onreadystatechange = null

			resolve(url)
		}

		document.head.appendChild(script)
	})
}

export function capitalizeFirstLetter(string)
{
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getFileList(e)
{
	let fileList = [];
	if (e.dataTransfer)
	{
		const dt = e.dataTransfer
		if (dt.files && dt.files.length)
			fileList = dt.files
		else if (dt.items && dt.items.length)
			fileList = dt.items
	} else if (e.target && e.target.files)
	{
		fileList = e.target.files
	}
	return fileList
}

export function getActionStateMessageTargets(actionName, type, targetDefault)
{
	let targets = []

	if (targetDefault?.hasAttribute && targetDefault?.hasAttribute('ui-message-' + type))
		targets.push(targetDefault)

	if (actionName)
	{
		document
			.querySelectorAll('[ui-action="' + actionName + '"][ui-message-' + type + '],[ui-state="' + actionName + '"][ui-message-' + type + ']')
			.forEach(t =>
			{
				if (targetDefault != t)
					targets.push(t)
			})
	}

	return targets
}

// Display ui-state messages for elemeents
//	requires .content[ui-message-{type}] and matching [ui-state] or [ui-action]
//	type = App.cssClass.fail or App.cssClass.success
export function setActionStateMessageForTargets(actionName, message, type, targetDefault)
{
	let targets = getActionStateMessageTargets(actionName, type, targetDefault)

	if (!App.Config.useDismissableAlerts)
		targets.forEach(t => t.setAttribute('ui-message-' + type, message))
	else
		targets.forEach(target =>
		{
			let elementMessage = target.querySelector('[ui-state-message]')
			if (!elementMessage)
			{
				// Create the element
				elementMessage = document.createElement('div')
				elementMessage.setAttribute('ui-state-message', '')
				target.prepend(elementMessage)
			}

			elementMessage.classList.add('alert', 'alert-dismissible', 'fade', 'in', (type == App.cssClass.fail ? 'alert-danger' : 'alert-success'))
			elementMessage.classList.remove('hidden', (type == App.cssClass.fail ? 'alert-success' : 'alert-danger'))
			elementMessage.setAttribute('role', 'alert')
			elementMessage.innerHTML = '<button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>' + message
		})

	return targets
}

export function checkoutToOrder(checkout)
{
	let paymentMethod = ''
	let walletCard = ''
	let shippingAddress = {}
	const shippingOption = App.Store.model('shippingoption', checkout?.attributes?.shippingMethod)?.attributes ?? {}

	if (checkout?.attributes?.shippingAddress)
	{
		shippingAddress = checkout.attributes.shippingAddress
	}
	else if (checkout?.relationships?.shippingAddress?.data?.id)
	{
		shippingAddress = App.Store.model(checkout.relationships.shippingAddress.data.type, checkout.relationships.shippingAddress.data.id)?.attributes
		shippingAddress.save = null
	}

	let shippingAddressSave = shippingAddress.save

	if (shippingAddress.isDefault && !shippingAddress.id)
		shippingAddressSave = 'true'

	if (checkout?.relationships?.paymentMethod?.data?.id)
	{
		paymentMethod = checkout.relationships.paymentMethod.data.id
		if (checkout?.attributes?.walletCard)
		{
			walletCard = checkout.attributes.walletCard
			if (walletCard.number && walletCard.hasOwnProperty('id'))
				delete walletCard.id // Precaution
		}
		else if (checkout.relationships.walletCard?.data?.id)
			walletCard = { id: checkout.relationships.walletCard.data.id }
	}
	else if (checkout?.attributes?.paymentMethodAndWalletCard)
	{
		const [paymentMethodId, walletCardId] = checkout.attributes.paymentMethodAndWalletCard.split('|')
		paymentMethod = paymentMethodId
		walletCard = { id: walletCardId }
	}

	return {
		// Payment Method
		paymentMethod,
		paymentMethodAdditionalFee: checkout?.attributes?.summary?.fee ?? 0,
		walletCard,
		billingAddress: checkout?.attributes?.billingAddress ?? '',
		// Shipping
		shippingAddress:
		{
			address1: shippingAddress.address1,
			address2: shippingAddress.address2,
			city: shippingAddress.city,
			stateProvince: shippingAddress.stateProvince,
			country: shippingAddress.country,
			postalCode: shippingAddress.postalCode,
			firstName: checkout?.attributes?.firstName || shippingAddress.firstName,
			lastName: checkout?.attributes?.lastName || shippingAddress.lastName,
			phone: checkout?.attributes?.phone || shippingAddress.phone,
			email: checkout?.attributes?.shippingEmail || shippingAddress.email,
			save: shippingAddressSave,
			isDefault: shippingAddress.isDefault
		},
		shipping: shippingOption.rate || '',
		shippingMethod: shippingOption.name || '',
		shippingMethodProvider: shippingOption.provider || '',
		specialInstructions: checkout?.attributes?.specialInstructions || '',
		// Tax/Totals
		subtotal: checkout?.attributes?.summary?.subtotal || '',
		tax: checkout?.attributes?.summary?.tax || '',
		total: checkout?.attributes?.summary?.total || ''
	}
}

export function loginRedirectUrl()
{
	return '/login?ReturnUrl=' + encodeURIComponent(location.pathname + location.search)
}